import { useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { GET_WORK_ORDER_OPERATION_CUSTOM_FIELDS } from 'lib/api/queries';

// The connector writes and clears pick shortfalls on its own cycle (5 min by
// default), so a 45 s poll shows a change well within one cycle.
export const WOO_CUSTOM_FIELDS_POLL_MS = 45000;

/**
 * Keeps the WOO's custom fields fresh while the component that calls it is
 * shown: polls the same query useAvailableMaterials reads, and refetches when
 * the tablet comes back to the app. The poll writes to the Apollo cache, so
 * every reader of the query (usePickingSummary, the materials log, the
 * coverage gate) sees connector changes without its own poll.
 */
const useRefreshWooCustomFields = (
  wooRef,
  { pollInterval = WOO_CUSTOM_FIELDS_POLL_MS } = {}
) => {
  const { refetch } = useQuery(GET_WORK_ORDER_OPERATION_CUSTOM_FIELDS, {
    variables: { workOrderOperationRef: wooRef },
    skip: !wooRef,
    fetchPolicy: 'cache-first',
    pollInterval,
  });

  useEffect(() => {
    if (!wooRef) return undefined;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      // A failed refresh leaves the cached fields in place; the poll retries.
      Promise.resolve(refetch()).catch(() => {});
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [wooRef, refetch]);
};

export default useRefreshWooCustomFields;
